import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import StyleIcon from "@mui/icons-material/Style";
import BusinessIcon from "@mui/icons-material/Business";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { inventoryManagementUrl, setupManagementUrl } from "../../../config";
import { ColorsQuery } from "../../../data/Axios/queries";
import { set } from "lodash";

const lightBlue = {
  main: "#5DADE2",
  light: "#85C1E9",
  lighter: "#AED6F1",
  dark: "#3498DB",
};

const ImprovedDetailsDialog = ({
  open,
  onClose,
  selectedProduct,
  inventoryDetails,
  isWarehouseMode,
  onAddToCart,
  lightBlue,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState("panel1");

  const [colorData, setColors] = useState([]);
  // Warehouse Mode States (Single carton quantity)
  const [warehouseCartons, setWarehouseCartons] = useState("");

  // Retail Mode States (Color-first approach with nested sizes)
  const [selectedColors, setSelectedColors] = useState([]);
  const [colorSizeCombinations, setColorSizeCombinations] = useState({});

  // Payment/Sale Date for backdating
  const [paymentDate, setPaymentDate] = useState("");
  const [isBackDated, setIsBackDated] = useState(false);

  // Distribution States
  const [currentBranchDistribution, setCurrentBranchDistribution] = useState(
    []
  );
  const [otherBranchesDistribution, setOtherBranchesDistribution] = useState(
    []
  );
  const [loadingDistribution, setLoadingDistribution] = useState(false);

  // Group inventory by color for better UX (retail mode only)
  const groupedInventory = !isWarehouseMode
    ? inventoryDetails.reduce((acc, item) => {
        const key = item.colorName;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {})
    : {};

  const colors = Object.keys(groupedInventory);

  // Reset states when dialog opens
  useEffect(() => {
    if (open) {
      setTabValue(0);
      setWarehouseCartons("");
      setPaymentDate("");
      setSelectedColors([]);
      setColorSizeCombinations({});
      setCurrentBranchDistribution([]);
      setOtherBranchesDistribution([]);
    }
  }, [open]);

  // Fetch distribution data
  const fetchDistribution = async () => {
    if (!selectedProduct) return;

    setLoadingDistribution(true);
    try {
      if (isWarehouseMode) {
        // Warehouse mode: Show distribution within current warehouse (by color/size)
        // The inventoryDetails already contains the current warehouse breakdown
        setCurrentBranchDistribution(inventoryDetails);
      } else {
        // Retail mode: Show current branch distribution and other branches
        setCurrentBranchDistribution(inventoryDetails);

        // Fetch other branches distribution
        const query = `{
          retailInventoryByColumnValues(inventory: { productCode: "${selectedProduct.productCode}", productId: ${selectedProduct.productId} }) {
            id
            branchId
            
            colorName
            size
            quantity
          }
        }`;

        const result = await graphqlQuery(query, inventoryManagementUrl.uri);

        if (result && result.length > 0) {
          // Group by branch and filter out current branch
          const branchGroups = {};
          result.forEach((item) => {
            // Skip current branch items
            if (item.branchName === inventoryDetails[0]?.branchName) return;

            if (!branchGroups[item.branchName]) {
              branchGroups[item.branchName] = {
                branchName: item.branchName,
                branchId: item.branchId,
                items: [],
                totalQuantity: 0,
              };
            }
            branchGroups[item.branchName].items.push(item);
            branchGroups[item.branchName].totalQuantity += item.quantity;
          });

          setOtherBranchesDistribution(Object.values(branchGroups));
        }
      }
    } catch (error) {
      console.error("Failed to fetch distribution:", error);
    } finally {
      setLoadingDistribution(false);
    }
  };

  const fetchColors = async () => {
    const query = ColorsQuery;
    try {
      const result = await graphqlQuery(query, setupManagementUrl.uri);
      setColors(result);
    } catch (error) {
      console.error("Error fetching colors:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchColors();
  }, []);

  // Color-First Approach Functions
  const addColor = (colorValue) => {
    if (!selectedColors.includes(colorValue)) {
      setSelectedColors([...selectedColors, colorValue]);
      setColorSizeCombinations({
        ...colorSizeCombinations,
        [colorValue]: [],
      });
    }
  };

  const removeColor = (colorValue) => {
    setSelectedColors(selectedColors.filter((c) => c !== colorValue));
    const newCombinations = { ...colorSizeCombinations };
    delete newCombinations[colorValue];
    setColorSizeCombinations(newCombinations);
  };

  const addSizeToColor = (colorValue) => {
    const currentSizes = colorSizeCombinations[colorValue] || [];
    setColorSizeCombinations({
      ...colorSizeCombinations,
      [colorValue]: [...currentSizes, { id: Date.now(), size: "", quantity: "" }],
    });
  };

  const removeSizeFromColor = (colorValue, sizeId) => {
    const currentSizes = colorSizeCombinations[colorValue] || [];
    setColorSizeCombinations({
      ...colorSizeCombinations,
      [colorValue]: currentSizes.filter((s) => s.id !== sizeId),
    });
  };

  const updateSizeInColor = (colorValue, sizeId, field, value) => {
    const currentSizes = colorSizeCombinations[colorValue] || [];
    setColorSizeCombinations({
      ...colorSizeCombinations,
      [colorValue]: currentSizes.map((s) =>
        s.id === sizeId ? { ...s, [field]: value } : s
      ),
    });
  };

  // Check if size already exists within a color
  const isDuplicateSize = (colorValue, currentSizeId, sizeValue) => {
    if (!sizeValue) return false;
    const currentSizes = colorSizeCombinations[colorValue] || [];
    return currentSizes.some(
      (s) => s.id !== currentSizeId && s.size === sizeValue && s.size !== ""
    );
  };

  // Validate and get available sizes for a color
  const getAvailableSizesForColor = (color) => {
    return (
      groupedInventory[color]?.map((item) =>
        isWarehouseMode ? item.sizeRange : item.size
      ) || []
    );
  };

  // Get available quantity for validation
  const getAvailableQuantity = (color, size) => {
    const item = inventoryDetails.find((detail) =>
      isWarehouseMode
        ? detail.colorName === color && detail.sizeRange === size
        : detail.colorName === color && detail.size === size
    );
    return item?.quantity || 0;
  };

  // Handle Add to Cart
  const handleAddToCart = () => {
    // One-liner for both date and time – always in user's local timezone
    const now = new Date();

    // YYYY-MM-DD  (local date, not UTC!)
    const todayDate = now.toLocaleDateString("en-CA"); // '2025-08-09'

    // HH:MM:SS  (24-hour, local time)
    const currentTime = now.toLocaleTimeString("en-GB", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }); // '14:35:22'

    // If item.createdDate is missing, use today
    const safeCreatedDate =
      paymentDate && paymentDate.trim() !== "" ? paymentDate : null;

    const combinedDateTime =
      paymentDate && paymentDate.trim() !== ""
        ? `${safeCreatedDate}T${currentTime}`
        : null;
    const isBackDated = safeCreatedDate === todayDate ? true : false;

    if (isWarehouseMode) {
      // Warehouse Mode: Add single entry with total cartons
      if (warehouseCartons && warehouseCartons > 0) {
        onAddToCart(selectedProduct, {
          quantity: warehouseCartons,
          isWarehouse: true,
          createdDate: combinedDateTime,
          isBaclkDated: isBackDated,
        });
      }
    } else {
      // Retail Mode: Add all valid combinations from color-first structure
      selectedColors.forEach((colorValue) => {
        const sizes = colorSizeCombinations[colorValue] || [];
        const validSizes = sizes.filter((s) => s.size && s.quantity > 0);

        validSizes.forEach((sizeEntry) => {
          const item = inventoryDetails.find(
            (d) => d.colorName === colorValue && d.size === sizeEntry.size
          );
          onAddToCart(selectedProduct, {
            ...item,
            selectedColor: colorValue,
            selectedSize: sizeEntry.size,
            quantity: sizeEntry.quantity,
            isExternal: false,
            createdDate: combinedDateTime,
            isBaclkDated: isBackDated,
          });
        });
      });
    }

    onClose();
  };

  // Check if form is valid
  const isFormValid = () => {
    if (isWarehouseMode) {
      return warehouseCartons > 0;
    } else {
      // Check if at least one color has at least one valid size with quantity
      return selectedColors.some((colorValue) => {
        const sizes = colorSizeCombinations[colorValue] || [];
        return sizes.some((s) => s.size && s.quantity > 0);
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: lightBlue.light, color: "white" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <StyleIcon sx={{ mr: 1 }} />
          {selectedProduct?.productName} ({selectedProduct?.productCode}{" "}
          {selectedProduct?.aliasProductName})
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, px: { xs: 2, md: 3 } }}>
        {/* Tabs for Main View and Distribution */}
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            if (
              newValue === 1 &&
              (currentBranchDistribution.length === 0 ||
                (!isWarehouseMode && otherBranchesDistribution.length === 0))
            ) {
              fetchDistribution();
            }
          }}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <WarehouseIcon sx={{ mr: 1 }} />
                {isWarehouseMode ? "Quantity Selection" : "Item Selection"}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SearchIcon sx={{ mr: 1 }} />
                {isWarehouseMode
                  ? "Warehouse Distribution"
                  : "Branch Distribution"}
              </Box>
            }
          />
        </Tabs>

        {/* Tab Panel 0: Main Selection */}
        {tabValue === 0 && (
          <Box>
            {/* Payment Date Field - Available in both modes */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: "#F0F4F8",
                border: "1px dashed #90A4AE",
              }}
            >
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Optional: Backdate Sale
              </Typography>
              <TextField
                label="Payment/Sale Date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Leave empty to use current date and time"
                sx={{ bgcolor: "white" }}
              />
            </Paper>

            {/* WAREHOUSE MODE */}
            {isWarehouseMode ? (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Warehouse Mode:</strong> Enter the number of cartons
                    you want to order for this product.
                  </Typography>
                </Alert>

                {/* Product Information Summary */}
                <Paper
                  elevation={0}
                  sx={{ p: 2, mb: 3, bgcolor: lightBlue.lighter }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Available
                      </Typography>
                      <Typography variant="h5" fontWeight="600">
                        {selectedProduct?.cartons} Cartons
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Price per Carton
                      </Typography>
                      <Typography variant="h5" fontWeight="600">
                        KSh {selectedProduct?.averageSP?.toLocaleString() || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Single Carton Quantity Input */}
                <Typography variant="h6" mb={2} fontWeight="600">
                  Enter Quantity
                </Typography>

                <TextField
                  label="Number of Cartons"
                  type="number"
                  value={warehouseCartons}
                  onChange={(e) =>
                    setWarehouseCartons(parseFloat(e.target.value) || "")
                  }
                  fullWidth
                  size="medium"
                  inputProps={{
                    min: 1,
                    step: 1,
                    max: selectedProduct?.cartons,
                  }}
                  error={warehouseCartons > selectedProduct?.cartons}
                  helperText={
                    warehouseCartons > selectedProduct?.cartons
                      ? `Cannot exceed available quantity (${selectedProduct?.cartons} cartons)`
                      : `Available: ${selectedProduct?.cartons} cartons`
                  }
                  sx={{ mb: 2 }}
                />

                {warehouseCartons > 0 && (
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>Total:</strong> {warehouseCartons} carton(s) × KSh{" "}
                      {selectedProduct?.averageSP?.toLocaleString() || 0} =
                      <strong>
                        {" "}
                        KSh{" "}
                        {(
                          warehouseCartons * (selectedProduct?.averageSP || 0)
                        ).toLocaleString()}
                      </strong>
                    </Typography>
                  </Alert>
                )}
              </Box>
            ) : (
              /* RETAIL MODE - Color-First Approach */
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Retail Mode:</strong> Click on colors below to select them,
                    then add sizes for each color. You can select multiple colors with
                    multiple sizes nested in each.
                  </Typography>
                </Alert>

                {/* Color Selection Section - Interactive Chips */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "#F0F4F8",
                    border: "1px dashed #90A4AE",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Step 1: Click to Select Colors
                  </Typography>

                  {/* Available Colors as Clickable Chips */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                    {colors.map((color) => {
                      const isSelected = selectedColors.includes(color);
                      const sizesCount = isSelected
                        ? colorSizeCombinations[color]?.filter(
                            (s) => s.size && s.quantity > 0
                          ).length || 0
                        : 0;

                      return (
                        <Chip
                          key={color}
                          label={
                            isSelected
                              ? `${color} (${sizesCount} size${sizesCount !== 1 ? 's' : ''})`
                              : color
                          }
                          onClick={() => {
                            if (!isSelected) {
                              addColor(color);
                            }
                          }}
                          onDelete={isSelected ? () => removeColor(color) : undefined}
                          color={isSelected ? "primary" : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            "&:hover": {
                              bgcolor: isSelected
                                ? lightBlue.dark
                                : lightBlue.lighter,
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        />
                      );
                    })}
                  </Box>

                  {colors.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No colors available for this product
                    </Alert>
                  )}
                </Paper>

                {/* Sizes for Each Color */}
                {selectedColors.length > 0 && (
                  <Typography variant="h6" mb={2} fontWeight="600">
                    Step 2: Add Sizes for Each Color
                  </Typography>
                )}

                {selectedColors.map((colorValue, colorIndex) => {
                  const sizesForColor = colorSizeCombinations[colorValue] || [];

                  return (
                    <Accordion
                      key={colorValue}
                      defaultExpanded={colorIndex === 0}
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          bgcolor: lightBlue.lighter,
                          "&:hover": { bgcolor: lightBlue.light },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            pr: 2,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="600">
                            {colorValue}
                          </Typography>
                          <Chip
                            label={`${sizesForColor.filter((s) => s.size && s.quantity > 0).length} sizes added`}
                            size="small"
                            color={
                              sizesForColor.some((s) => s.size && s.quantity > 0)
                                ? "success"
                                : "default"
                            }
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {sizesForColor.map((sizeEntry, sizeIndex) => {
                          const isDuplicate = isDuplicateSize(
                            colorValue,
                            sizeEntry.id,
                            sizeEntry.size
                          );
                          const availableQty = sizeEntry.size
                            ? getAvailableQuantity(colorValue, sizeEntry.size)
                            : 0;

                          return (
                            <Paper
                              key={sizeEntry.id}
                              elevation={1}
                              sx={{
                                p: 2,
                                mb: 2,
                                bgcolor: isDuplicate ? "#FFF3E0" : "white",
                                border: isDuplicate
                                  ? "2px solid #FF9800"
                                  : "1px solid #e0e0e0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 2,
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Size Entry {sizeIndex + 1}
                                </Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    removeSizeFromColor(colorValue, sizeEntry.id)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              {isDuplicate && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                  This size is already selected for this color.
                                  Please choose a different size or remove this
                                  entry.
                                </Alert>
                              )}

                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Size</InputLabel>
                                    <Select
                                      value={sizeEntry.size}
                                      onChange={(e) =>
                                        updateSizeInColor(
                                          colorValue,
                                          sizeEntry.id,
                                          "size",
                                          e.target.value
                                        )
                                      }
                                      label="Size"
                                    >
                                      {getAvailableSizesForColor(colorValue).map(
                                        (size) => {
                                          const available = getAvailableQuantity(
                                            colorValue,
                                            size
                                          );
                                          return (
                                            <MenuItem
                                              key={size}
                                              value={size}
                                              disabled={available === 0}
                                            >
                                              {size} ({available} available)
                                            </MenuItem>
                                          );
                                        }
                                      )}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Quantity (Pairs)"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={sizeEntry.quantity}
                                    onChange={(e) =>
                                      updateSizeInColor(
                                        colorValue,
                                        sizeEntry.id,
                                        "quantity",
                                        parseFloat(e.target.value) || ""
                                      )
                                    }
                                    disabled={!sizeEntry.size || isDuplicate}
                                    inputProps={{
                                      min: 1,
                                      max: availableQty,
                                    }}
                                    error={sizeEntry.quantity > availableQty}
                                    helperText={
                                      isDuplicate
                                        ? "Remove duplicate first"
                                        : sizeEntry.quantity > availableQty
                                        ? "Exceeds available quantity"
                                        : sizeEntry.size
                                        ? `Max: ${availableQty}`
                                        : "Select size first"
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Paper>
                          );
                        })}

                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => addSizeToColor(colorValue)}
                          variant="outlined"
                          fullWidth
                          size="small"
                          sx={{
                            borderStyle: "dashed",
                            borderWidth: 2,
                            color: lightBlue.dark,
                            borderColor: lightBlue.dark,
                            "&:hover": {
                              borderColor: lightBlue.main,
                              bgcolor: lightBlue.lighter,
                            },
                          }}
                        >
                          Add Size to {colorValue}
                        </Button>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {selectedColors.length === 0 && (
                  <Alert severity="info">
                    Start by selecting a color from the dropdown above
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Tab Panel 1: Distribution */}
        {tabValue === 1 && (
          <Box>
            {loadingDistribution ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : isWarehouseMode ? (
              /* WAREHOUSE DISTRIBUTION - Show breakdown by color/size */
              <Box>
                <Typography variant="h6" mb={2} fontWeight="600">
                  Warehouse Stock Distribution
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This shows how quantities are distributed across colors and
                  size ranges in this warehouse.
                </Alert>

                {currentBranchDistribution.length === 0 ? (
                  <Alert severity="warning">
                    No distribution data available
                  </Alert>
                ) : (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: 1, borderColor: "divider" }}
                  >
                    <Table size="small">
                      <TableHead sx={{ bgcolor: lightBlue.lighter }}>
                        <TableRow>
                          <TableCell>
                            <strong>Color</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Size Range</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Cartons</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Qty/Carton</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Total Pairs</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentBranchDistribution.map((item) => (
                          <TableRow
                            key={item.id}
                            sx={{
                              "&:hover": { bgcolor: lightBlue.lighter },
                              bgcolor:
                                item.quantity === 0 ? "#FADBD8" : "inherit",
                            }}
                          >
                            <TableCell>{item.colorName}</TableCell>
                            <TableCell>{item.sizeRange}</TableCell>
                            <TableCell align="right">{item.cartons}</TableCell>
                            <TableCell align="right">
                              {item.quantityPerCarton}
                            </TableCell>
                            <TableCell align="right">
                              <strong>
                                {item.cartons * item.quantityPerCarton}
                              </strong>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Total Row */}
                        <TableRow
                          sx={{
                            bgcolor: lightBlue.light,
                            "& td": {
                              fontWeight: "bold",
                              fontSize: "1rem",
                              borderTop: 2,
                              borderColor: "divider",
                            },
                          }}
                        >
                          <TableCell colSpan={2}>
                            <strong>TOTAL</strong>
                          </TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">-</TableCell>
                          <TableCell align="right">
                            {currentBranchDistribution.reduce(
                              (sum, item) =>
                                sum +
                                (item.cartons || 0) *
                                  (item.quantityPerCarton || 0),
                              0
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : (
              /* RETAIL DISTRIBUTION - Show current branch and other branches */
              <Box>
                <Tabs
                  value={tabValue === 1 ? 0 : 0}
                  onChange={(e, newValue) => {
                    // Sub-tabs within distribution tab
                  }}
                  sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                >
                  <Tab label="Current Branch" />
                  <Tab label="Other Branches" />
                </Tabs>

                {/* Always show current branch by default */}
                <Box>
                  <Typography variant="h6" mb={2} fontWeight="600">
                    Current Branch Distribution
                  </Typography>

                  {currentBranchDistribution.length === 0 ? (
                    <Alert severity="warning">No items in current branch</Alert>
                  ) : (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "space-between",
                            pr: 2,
                          }}
                        >
                          <Typography fontWeight="600">
                            {currentBranchDistribution[0]?.branchName ||
                              "Current Branch"}
                          </Typography>
                          <Chip
                            label={`${currentBranchDistribution.reduce(
                              (sum, item) => sum + item.quantity,
                              0
                            )} pairs`}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Group by color for better display */}
                        {Object.entries(
                          currentBranchDistribution.reduce((acc, item) => {
                            if (!acc[item.colorName]) acc[item.colorName] = [];
                            acc[item.colorName].push(item);
                            return acc;
                          }, {})
                        ).map(([color, items]) => {
                          // Calculate total for this color
                          const colorTotal = items.reduce(
                            (sum, item) => sum + item.quantity,
                            0
                          );

                          return (
                            <Accordion key={color} sx={{ mb: 1 }}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ bgcolor: lightBlue.lighter }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    width: "100%",
                                    justifyContent: "space-between",
                                    pr: 2,
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="600"
                                  >
                                    {color}
                                  </Typography>
                                  <Chip
                                    label={`${colorTotal} pairs`}
                                    size="small"
                                    color="primary"
                                  />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <TableContainer component={Paper} elevation={0}>
                                  <Table size="small">
                                    <TableHead
                                      sx={{ bgcolor: lightBlue.lighter }}
                                    >
                                      <TableRow>
                                        <TableCell>
                                          <strong>Size</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>Quantity</strong>
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {items.map((item) => (
                                        <TableRow key={item.id}>
                                          <TableCell>{item.size}</TableCell>
                                          <TableCell align="right">
                                            <Chip
                                              label={item.quantity}
                                              size="small"
                                              color={
                                                item.quantity === 0
                                                  ? "error"
                                                  : item.quantity < 5
                                                  ? "warning"
                                                  : "success"
                                              }
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      {/* Total Row for Color */}
                                      <TableRow
                                        sx={{
                                          bgcolor: lightBlue.light,
                                          "& td": {
                                            fontWeight: "bold",
                                            borderTop: 2,
                                            borderColor: "divider",
                                          },
                                        }}
                                      >
                                        <TableCell>
                                          <strong>Total</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Chip
                                            label={colorTotal}
                                            size="small"
                                            color="primary"
                                            sx={{ fontWeight: "bold" }}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  <Divider sx={{ my: 3 }} />

                  {/* Other Branches */}
                  <Typography variant="h6" mb={2} fontWeight="600">
                    Distribution in Other Branches
                  </Typography>

                  {otherBranchesDistribution.length === 0 ? (
                    <Alert severity="info">
                      No stock found in other branches for this product
                    </Alert>
                  ) : (
                    <Box>
                      {otherBranchesDistribution.map((branch) => (
                        <Accordion key={branch.branchId} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                                justifyContent: "space-between",
                                pr: 2,
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <StorefrontIcon
                                  sx={{ mr: 1, color: lightBlue.main }}
                                />
                                <Typography fontWeight="600">
                                  {branch.branchName}
                                </Typography>
                              </Box>
                              <Chip
                                label={`${branch.totalQuantity} pairs`}
                                color="primary"
                                size="small"
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {/* Group by color */}
                            {Object.entries(
                              branch.items.reduce((acc, item) => {
                                if (!acc[item.colorName])
                                  acc[item.colorName] = [];
                                acc[item.colorName].push(item);
                                return acc;
                              }, {})
                            ).map(([color, items]) => (
                              <Box key={color} sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="600"
                                  gutterBottom
                                >
                                  {color}
                                </Typography>
                                <TableContainer component={Paper} elevation={0}>
                                  <Table size="small">
                                    <TableHead
                                      sx={{ bgcolor: lightBlue.lighter }}
                                    >
                                      <TableRow>
                                        <TableCell>
                                          <strong>Size</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>Quantity</strong>
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {items.map((item, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>{item.size}</TableCell>
                                          <TableCell align="right">
                                            <Chip
                                              label={item.quantity}
                                              size="small"
                                              color={
                                                item.quantity === 0
                                                  ? "error"
                                                  : item.quantity < 5
                                                  ? "warning"
                                                  : "success"
                                              }
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#F8F9FA" }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {tabValue === 0 && (
          <Button
            onClick={handleAddToCart}
            variant="contained"
            disabled={!isFormValid()}
            sx={{
              bgcolor: lightBlue.main,
              "&:hover": {
                bgcolor: lightBlue.dark,
              },
            }}
          >
            Add to Cart
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImprovedDetailsDialog;
